import React, {ReactNode, useRef} from 'react'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Icon} from '@vio/atlas/Icon'
import {OverlayPage, OverlayPageContent} from '@vio/atlas/OverlayPage'
import {PopUp} from '@vio/atlas/PopUp'
import {cssTheme} from '@vio/atlas-tokens'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {LinkInterceptor} from '@vio/core/src/_web/utils/embedded/components'
import {
  BookingError,
  Psd2AuthStatusErrorType
} from '@vio/core/src/booking/types/ErrorResponse'
import {isFlutterAppWebView} from '@vio/core/src/native'

import {RecoverableErrorContentLayout} from './RecoverableErrorContentLayout'
import {SoldOutErrorContentLayout} from './SoldOutErrorContentLayout'

const POPUP_WIDTH = 480

const Wrapper = styled.div`
  padding-top: ${cssTheme.layout.spacing.s700};
`

export enum ErrorType {
  Recoverable = 'recoverable',
  NonRecoverable = 'non_recoverable'
}

interface PropsType {
  isOpen: boolean
  checkIn: string
  checkOut: string
  rooms: string
  onClick: () => void
  onShadeBackgroundClick?: () => void
  closeOnOutsideClick?: boolean
  bookingError?: BookingError | Psd2AuthStatusErrorType
  redirectOffersSlot: ReactNode
  hotelCardSlot: ReactNode
  isPayAtProperty?: boolean
}

export const AlternativeOffersOverlay = ({
  isOpen,
  checkIn,
  checkOut,
  rooms,
  onClick,
  onShadeBackgroundClick = () => {},
  closeOnOutsideClick = true,
  bookingError,
  redirectOffersSlot,
  hotelCardSlot,
  isPayAtProperty = false
}: PropsType) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const {isMobile} = useDeviceLayout()
  const isEmbedded = useIsEmbeddedWithBrand()

  const props = {
    checkIn,
    checkOut,
    rooms,
    onClick,
    redirectOffersSlot,
    hotelCardSlot,
    isPayAtProperty
  }

  const Content = bookingError ? (
    <RecoverableErrorContentLayout {...props} bookingError={bookingError} />
  ) : (
    <SoldOutErrorContentLayout {...props} />
  )

  return isMobile ? (
    <OverlayPage
      showCloseButton={false}
      onShadeBackgroundClick={onShadeBackgroundClick}
      isOpen={isOpen}
      navBarProps={{
        startContent: isFlutterAppWebView() ? null : (
          <Button variant="ghost" onClick={onClick}>
            <Icon name="ChevronLeft" />
          </Button>
        )
      }}
      hasHeaderBoxShadow={false}
    >
      <OverlayPageContent ref={containerRef}>
        {isEmbedded && <LinkInterceptor containerRef={containerRef} />}
        {Content}
      </OverlayPageContent>
    </OverlayPage>
  ) : (
    <PopUp
      width={POPUP_WIDTH}
      isOpen={isOpen}
      closeAction={onClick}
      closeOnOutsideClick={closeOnOutsideClick}
    >
      <Wrapper ref={containerRef}>
        {Content}
        {isEmbedded && <LinkInterceptor containerRef={containerRef} />}
      </Wrapper>
    </PopUp>
  )
}
